import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@shared/config/routes';
import { isMedia } from '@shared/lib/html';
import { Button } from '@shared/ui/button';
import { DropDown } from '@shared/ui/dropdown';
import { ModalWrapper } from '@shared/ui/modal-wrapper';
import { requestLogout, useAuth } from '@features/auth';
import { logoutSip } from '@features/call';
import IconX from '@assets/img/icons/close-x.svg';
import IconUserDefault from '@assets/img/user-ava.svg';

import styles from './styles.module.scss';

const UserComponent: FC = () => {
	const navigate = useNavigate();
	const { user, resetAuth } = useAuth();
	const [isOpened, setIsOpened] = useState(false);

	const refTrigger = useRef<HTMLDivElement>(null);
	const refDropdown = useRef<HTMLDivElement>(null);

	const isMobile = isMedia('sm');
	const userName = user?.name || 'Пользователь';

	const closeMenu = useCallback(() => {
		setIsOpened(false);
	}, []);

	const toggleMenu = useCallback(() => {
		setIsOpened((prev) => !prev);
	}, []);

	const handleLogout = useCallback(async () => {
		setIsOpened(false);

		try {
			await logoutSip();
			await requestLogout();
		} finally {
			resetAuth();
			await navigate(ROUTES.LOGIN, { replace: true });
		}
	}, [navigate, resetAuth]);

	useEffect(() => {
		if (!isOpened || isMobile) return;

		const handleDocumentClick = (event: MouseEvent) => {
			const target = event.target as Node;
			if (refTrigger.current?.contains(target) || refDropdown.current?.contains(target)) {
				return;
			}
			setIsOpened(false);
		};

		document.addEventListener('mousedown', handleDocumentClick);

		return () => {
			document.removeEventListener('mousedown', handleDocumentClick);
		};
	}, [isOpened, isMobile]);

	useEffect(() => {
		if (!isOpened) return;

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setIsOpened(false);
			}
		};

		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('keydown', handleEscape);
		};
	}, [isOpened]);

	const ava = (
		<div
			className={styles.Ava}
			// SVG инлайн‑код
			dangerouslySetInnerHTML={{ __html: IconUserDefault }}
		/>
	);

	return (
		<div className={styles.User}>
			<div
				ref={refTrigger}
				className={styles.Info}
				role="button"
				tabIndex={0}
				onClick={toggleMenu}
				onKeyDown={(event) => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						toggleMenu();
					}
				}}
			>
				<div className={styles.AvaWrap}>{ava}</div>
				<div className={styles.Text}>{userName}</div>
			</div>

			{isOpened && !isMobile && (
				<DropDown ref={refDropdown} disableMinWidth floatRight offsetTop={4} refParent={refTrigger}>
					<div className={styles.Menu}>
						<button className={styles.MenuItem} type="button" onClick={handleLogout}>
							Выйти
						</button>
					</div>
				</DropDown>
			)}

			{isOpened && isMobile && (
				<ModalWrapper>
					<div className={styles.Popup}>
						<div className={styles.PopupHeader}>
							<div className={styles.PopupTitle}>{userName}</div>
							<Button
								buttonType="icon"
								className={styles.PopupClose}
								svg={IconX}
								onClick={closeMenu}
							/>
						</div>
						<div className={styles.PopupMenu}>
							<button className={styles.MobileMenuItem} type="button" onClick={handleLogout}>
								Выйти
							</button>
						</div>
					</div>
				</ModalWrapper>
			)}
		</div>
	);
};

export const User = memo(UserComponent);
